import { useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import { modalPrimaryButton } from "../../../components/ui/buttonStyles.js";
import { AssetModalField } from "./AssetModalField.jsx";
import {
  ASSET_FORM_EMPTY,
  assetFormToPayload,
  assetToForm,
  formatColumnLabel,
  validateAssetStep,
} from "../assetUtils.js";
import { getAssetTypeAppMeta, getAssetTypeAssetMeta } from "../assetTypeUtils.js";

const TOTAL_STEPS = 2;
const STEP_SUBTITLES = {
  1: "Step 1 — Asset type & app meta",
  2: "Step 2 — Asset meta details",
};

function renderMetaFields(columns, form, errors, setValue) {
  if (columns.length === 0) return null;
  return columns.map((col) => (
    <AssetModalField
      key={col.key}
      label={formatColumnLabel(col.key)}
      value={form.values?.[col.key] ?? ""}
      onChange={(value) => setValue(col.key, value)}
      placeholder={`Enter ${formatColumnLabel(col.key).toLowerCase()}`}
      error={errors.values?.[col.key]}
      icon={col.type === "int" ? "grid" : col.type === "file" ? "layers" : "model"}
      type={col.type === "int" ? "number" : "text"}
    />
  ));
}

export function CreateAssetModal({
  open,
  onClose,
  onSave,
  editing = null,
  saving = false,
  assetTypes = [],
}) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...ASSET_FORM_EMPTY, values: {} });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const isEdit = Boolean(editing);

  const selectedAssetType = useMemo(
    () => assetTypes.find((type) => type.id === form.assetTypeId) ?? null,
    [assetTypes, form.assetTypeId],
  );

  const appMetaColumns = useMemo(
    () => getAssetTypeAppMeta(selectedAssetType),
    [selectedAssetType],
  );

  const assetMetaColumns = useMemo(
    () => getAssetTypeAssetMeta(selectedAssetType),
    [selectedAssetType],
  );

  useLayoutEffect(() => {
    if (!open) return;
    setStep(1);
    setForm(assetToForm(editing));
    setErrors({});
    setSubmitError(null);
  }, [open, editing]);

  if (!open) return null;

  const setValue = (key, value) => {
    setForm((prev) => ({
      ...prev,
      values: { ...prev.values, [key]: value },
    }));
  };

  const handleAssetTypeChange = (assetTypeId) => {
    const nextType = assetTypes.find((type) => type.id === assetTypeId);
    const nextValues = {};
    [...getAssetTypeAppMeta(nextType), ...getAssetTypeAssetMeta(nextType)].forEach((col) => {
      nextValues[col.key] = form.values?.[col.key] ?? "";
    });
    setForm((prev) => ({ ...prev, assetTypeId, values: nextValues }));
  };

  const handleNext = () => {
    const nextErrors = validateAssetStep(form, 1, selectedAssetType);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStep(2);
  };

  const handleBack = () => {
    setErrors({});
    setSubmitError(null);
    if (step === 1) {
      onClose();
      return;
    }
    setStep(1);
  };

  const handleSubmit = async () => {
    const nextErrors = validateAssetStep(form, 2, selectedAssetType);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitError(null);
    try {
      await onSave(assetFormToPayload(form, selectedAssetType));
      onClose();
    } catch (err) {
      setSubmitError(err.message ?? "Failed to save asset");
    }
  };

  const typeOptions = assetTypes.map((type) => ({ value: type.id, label: type.name }));

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="asset-modal-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(620px, 96vw)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 16,
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          boxShadow: ap.cardShadowLg,
          zIndex: 71,
        }}
      >
        <ModalShellHeader
          title={isEdit ? "Edit asset" : "Add Asset"}
          titleId="asset-modal-title"
          subtitle={STEP_SUBTITLES[step]}
          icon={Ico.plus}
          onClose={onClose}
          closeLabel="Close asset form"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
          trailing={(
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: A, background: `${A}18`, border: `1px solid ${A}33`, borderRadius: 999, padding: "6px 10px" }}>
              {step} / {TOTAL_STEPS}
            </div>
          )}
        />

        <div style={{ padding: 24, overflowY: "auto", flex: 1, minHeight: 0 }}>
          {step === 1 && (
            <>
              <AssetModalField
                label="Asset type"
                value={form.assetTypeId}
                onChange={handleAssetTypeChange}
                placeholder="Select asset type"
                error={errors.assetTypeId}
                icon="layers"
                as="select"
                options={typeOptions}
                disabled={isEdit}
              />
              {assetTypes.length === 0 && (
                <p style={{ margin: "0 0 16px", fontSize: 12, color: c.sub, lineHeight: 1.5 }}>
                  Create an asset type first to define fields for new assets.
                </p>
              )}
              {form.assetTypeId && appMetaColumns.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: c.sub, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 12 }}>
                    App meta
                  </div>
                  {renderMetaFields(appMetaColumns, form, errors, setValue)}
                </div>
              )}
              {form.assetTypeId && appMetaColumns.length === 0 && (
                <p style={{ margin: "0 0 16px", fontSize: 12, color: c.sub, lineHeight: 1.5 }}>
                  This asset type has no app meta fields.
                </p>
              )}
            </>
          )}

          {step === 2 && (
            <>
              {assetMetaColumns.length === 0 ? (
                <p style={{ margin: "0 0 16px", fontSize: 12, color: c.sub, lineHeight: 1.5 }}>
                  This asset type has no asset meta fields. You can still create the asset with the information from step 1.
                </p>
              ) : (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: c.sub, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 12 }}>
                    Asset meta
                  </div>
                  {renderMetaFields(assetMetaColumns, form, errors, setValue)}
                </>
              )}
            </>
          )}

          {submitError && (
            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#EF4444", fontWeight: 600 }}>{submitError}</p>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button type="button" onClick={handleBack} disabled={saving} style={outlineBtn(c, dark)}>
              {step === 1 ? "Cancel" : "Back"}
            </button>
            {step < TOTAL_STEPS ? (
              <button type="button" onClick={handleNext} style={modalPrimaryButton(A, dark)}>
                Next
              </button>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={saving} style={modalPrimaryButton(A, dark, saving)}>
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create asset"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function outlineBtn(c, dark) {
  return {
    height: 44,
    padding: "0 20px",
    borderRadius: 10,
    border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
    background: "transparent",
    color: c.text,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  };
}
